// HU-16 · HU-18 · Numeración y aprobación del FAR-03 (rol aprobador: VP / Gerencia).
//
// LA COLA DEL APROBADOR LISTA INFORMES, NO CASOS DE LA BANDEJA (14-sep-2026). Hasta hoy
// esta pantalla era una lectura filtrada de `GET /evaluacion/bandeja`, y eso tenía un
// hueco: la bandeja solo trae los casos que el Comité tiene entre manos, así que un
// informe en revisión cuyo caso ya salió de ella —o cuyo estado no era `en_aprobacion`—
// no se veía, y el aprobador no se enteraba. `GET /evaluacion/informes` trae cada
// informe con su estado, su versión, quién lo envió y cuándo. Dos bloques:
//
//   · Por aprobar                     `en_revision`: enviado, espera decisión (C-05)
//   · Observados, esperando al Comité `observado`: el aprobador lo devolvió y el Comité subsana
//
// REPLIEGUE: si el servidor todavía no expone el endpoint (404, backend viejo), la
// pantalla vuelve a leer la bandeja como antes y lo dice con un aviso discreto, para que
// nadie confunda «no hay informes» con «no se están viendo todos».
//
// Abrir un caso lleva a la evaluación en SOLO LECTURA con la pestaña «Informe FAR-03»,
// donde está la versión congelada y la tarjeta «Aprobación».
//
// SPRINT 3 (`ui/MG-ILE-43.md`, tarea 17-5): los seis estados con su testid, los dos bloques
// y la fila con el de la spec, el aviso de éxito al volver de aprobar u observar (con «Ver
// expediente» tras aprobar) y el 403 como «Sin permiso» con salida a la bandeja. La pantalla
// ya no dice que aprobar le da el código al informe (HU-17-C4): el código es el número del
// caso desde que se elabora (RN-07).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comoFechaDeInstante } from '../lib/fechas';
import { FileCheck2, FolderOpen, Inbox, RefreshCw, Search, ShieldX } from 'lucide-react';
import { Alert, Button, Card, Codigo, EmptyState, Input, Mono, PageHeader, StatusBadge } from '../components/ui';
import type { Tono } from '../components/ui';
import Bandera from '../components/Banderas';
import { consultarBandeja } from '../api/bandeja';
import type { FilaDeBandeja } from '../api/bandeja';
import { listarInformesEnAprobacion } from '../api/evaluacion';
import type { InformeEnAprobacion } from '../api/evaluacion';
import { ErrorDeApi, aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { estadoDelInforme, etiquetaDelEstadoDelCaso } from '../lib/etiquetas';
import { useDiferido } from '../lib/useDiferido';
import { useZonaDelPais } from '../lib/CatalogosContexto';
import type { DecisionDelAprobador } from '../components/AprobacionDelInforme';

// DD/MM/AAAA EN TODA LA INTERFAZ (25-sep-2026). Antes recortaba el ISO y lo mostraba
// tal cual, asi que en la misma fila convivian «2026-09-24» y «25/09/26». RN-82: el día se
// toma en la zona del país del caso.
const fecha = (iso: unknown, zona: string | null): string => comoFechaDeInstante(iso, zona) || '—';

/** Los estados del caso que le tocan al aprobador (solo para el repliegue a la bandeja). */
const ESTADOS_DEL_APROBADOR = new Set(['en_aprobacion', 'observado']);

/** En la bandeja, observado = ya lo devolvió el aprobador; el resto de los `en_aprobacion`, por aprobar. */
const estaObservadoEnLaBandeja = (f: FilaDeBandeja): boolean =>
  f.estado_del_caso === 'observado' || Boolean(f.motivo_de_observacion) || /subsanar/i.test(f.accion_pendiente ?? '');

/**
 * Una fila de la bandeja, vestida de informe. Lo que la bandeja no sabe —versión,
 * quién lo envió, cuándo— queda en null y la tabla lo pinta con «—».
 */
const desdeLaBandeja = (f: FilaDeBandeja): InformeEnAprobacion => ({
  caso_id: f.caso_id,
  numero_de_caso: f.numero,
  pais_evento: f.pais,
  nombre_pais: f.nombre_pais,
  producto: f.producto,
  gravedad: f.gravedad,
  estado_del_caso: f.estado_del_caso,
  informe_id: '',
  version: null,
  estado_del_informe: estaObservadoEnLaBandeja(f) ? 'observado' : 'en_revision',
  enviado_por: null,
  enviado_en: null,
  motivo_de_observacion: f.motivo_de_observacion,
  plazo_interno: f.texto_del_plazo ? { estado: f.estado_del_plazo, texto: f.texto_del_plazo } : null,
  accion_pendiente: f.accion_pendiente,
});

/** El tono del plazo interno por su clave; lo que no se conoce, neutro (RNF-11: texto e ícono, no solo color). */
const TONO_DEL_PLAZO: Record<string, Tono> = {
  vencido: 'danger',
  por_vencer: 'warning',
  en_plazo: 'success',
  cumplido: 'success',
  detenido: 'neutral',
};

const esNoEncontrado = (e: unknown): boolean => e instanceof ErrorDeApi && e.estado === 404;

const contieneElNumero = (fila: InformeEnAprobacion, numero: string): boolean =>
  !numero || (fila.numero_de_caso ?? '').toLowerCase().includes(numero.trim().toLowerCase());

function Fila({ fila, onAbrir, onRevisar }: {
  fila: InformeEnAprobacion; onAbrir?: (casoId: string) => void; onRevisar: (casoId: string) => void;
}) {
  const numero = fila.numero_de_caso ?? fila.caso_id.slice(0, 8);
  const zona = useZonaDelPais()(fila.pais_evento);
  const tonoDelPlazo = fila.plazo_interno ? (TONO_DEL_PLAZO[fila.plazo_interno.estado] ?? 'neutral') : 'neutral';
  return (
    <tr data-testid="aprobacion-cola-fila" className="border-t border-hairline hover:bg-slate-50">
      <td className="px-4 py-2.5 align-top">
        <Codigo className="whitespace-nowrap">{numero}</Codigo>
        {/* ui/MG-ILE-43 · la cola lista INFORMES: su estado con el texto del servidor
            (`texto_del_estado_del_informe`, el mismo que `aprobacion-estado`). El repliegue a la
            bandeja (servidor anterior) no lo trae y dice el estado del caso, como antes. */}
        {fila.texto_del_estado_del_informe ? (
          <div className="mt-1" data-testid="aprobacion-cola-fila-estado">
            <StatusBadge tone={estadoDelInforme(fila.estado_del_informe).tono}>{fila.texto_del_estado_del_informe}</StatusBadge>
          </div>
        ) : fila.estado_del_caso && (
          <div className="text-meta text-ink-soft mt-0.5">{etiquetaDelEstadoDelCaso(fila.estado_del_caso)}</div>
        )}
      </td>
      <td className="px-4 py-2.5 align-top">
        <span className="inline-flex items-center gap-2 text-compacto">
          <Bandera cod={fila.pais_evento} nombre={fila.nombre_pais ?? fila.pais_evento} />{fila.nombre_pais ?? fila.pais_evento}
        </span>
      </td>
      <td className="px-4 py-2.5 align-top">{fila.producto ?? <span className="text-ink-soft">—</span>}</td>
      <td className="px-4 py-2.5 align-top">
        {fila.gravedad === 'grave' && <StatusBadge tone="danger">Grave</StatusBadge>}
        {fila.gravedad === 'no_grave' && <StatusBadge tone="neutral">No grave</StatusBadge>}
        {fila.gravedad === null && <span className="text-ink-soft" role="img" aria-label="Gravedad sin decidir">—</span>}
      </td>
      <td className="px-4 py-2.5 align-top whitespace-nowrap">
        {fila.version ? <Mono className="text-meta">v{fila.version}</Mono> : <span className="text-ink-soft">—</span>}
        {fila.ronda ? <div className="text-meta text-ink-soft mt-0.5">ronda {fila.ronda}</div> : null}
      </td>
      <td className="px-4 py-2.5 align-top">{fila.enviado_por ?? <span className="text-ink-soft">—</span>}</td>
      <td className="px-4 py-2.5 align-top whitespace-nowrap"><Mono className="text-meta">{fecha(fila.enviado_en, zona)}</Mono></td>
      <td className="px-4 py-2.5 align-top">
        {fila.plazo_interno
          ? <StatusBadge tone={tonoDelPlazo}>{fila.plazo_interno.texto}</StatusBadge>
          : <span className="text-ink-soft">—</span>}
      </td>
      <td className="px-4 py-2.5 align-top">
        <span className="text-compacto">{fila.accion_pendiente ?? (fila.estado_del_informe === 'observado' ? 'Subsanar observación' : 'Esperando aprobación')}</span>
        {fila.motivo_de_observacion && (
          <p className="text-meta text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1 max-w-xs">{fila.motivo_de_observacion}</p>
        )}
        {fila.observado_en && (
          <div className="text-meta text-ink-soft mt-0.5">Observado el <Mono className="text-meta">{fecha(fila.observado_en, zona)}</Mono></div>
        )}
      </td>
      <td className="px-4 py-2.5 align-top text-right whitespace-nowrap">
        <Button size="sm" icon={FileCheck2} onClick={() => onRevisar(fila.caso_id)} aria-label={`Revisar el informe de ${fila.numero_de_caso ?? 'el caso'}`} className="mr-2">
          Revisar informe
        </Button>
        {onAbrir && (
          <Button size="sm" variant="secondary" icon={FolderOpen} onClick={() => onAbrir(fila.caso_id)} aria-label={`Abrir ${fila.numero_de_caso ?? 'el caso'} en solo lectura`}>
            Abrir
          </Button>
        )}
      </td>
    </tr>
  );
}

const COLUMNAS = ['N.º de caso', 'País', 'Producto', 'Gravedad', 'Versión', 'Enviado por', 'Enviado el', 'Plazo interno', 'Acción pendiente'];

/**
 * Un bloque SIEMPRE se pinta, con su cuenta en el título; vacío, explica por qué. Que un
 * bloque desaparezca cuando no tiene filas se lee como «esto no existe», y el aprobador
 * necesita ver que hay DOS colas aunque una esté a cero.
 */
function Bloque({ titulo, filas, vacio, onAbrir, onRevisar, testid }: {
  titulo: string; filas: InformeEnAprobacion[]; vacio: { titulo: string; descripcion: string; icono: typeof Inbox; testid?: string };
  onAbrir?: (casoId: string) => void; onRevisar: (casoId: string) => void;
  /** `aprobacion-bloque-por-aprobar` o `aprobacion-bloque-observados` (spec MG-ILE-43). */
  testid: string;
}) {
  return (
    <Card data-testid={testid} pad={false} className="mb-4" title={`${titulo} (${filas.length})`}>
      {/* Con filas, la seccion con nombre ES la zona que desplaza (WCAG 1.4.10 y 2.1.1, auditoria del 2-oct-2026).
          `relative`: recorta lo `absolute` de sus celdas (un `sr-only`), que si no agranda la pagina (BUG-1099). */}
      <section aria-label={`${titulo} (${filas.length})`} className={filas.length ? 'relative overflow-x-auto max-h-[70vh]' : undefined}>
        {filas.length === 0 ? (
          <EmptyState data-testid={vacio.testid} icon={vacio.icono} title={vacio.titulo} description={vacio.descripcion} />
        ) : (
          <div>
            <table className="w-full text-compacto">
              <thead className="bg-canvas sticky top-0 z-[1]">
                <tr>
                  {COLUMNAS.map((c) => (
                    <th key={c} scope="col" className="font-semibold text-meta text-ink-soft px-4 py-2.5 whitespace-nowrap text-left">{c}</th>
                  ))}
                  <th scope="col" className="px-4 py-2.5"><span className="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => <Fila key={f.informe_id || f.caso_id} fila={f} onAbrir={onAbrir} onRevisar={onRevisar} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Card>
  );
}

export default function AprobacionDeInformes({ onAbrirCaso, onRevisar, exito = null, onCerrarExito, onIrABandeja }: {
  /** Abre el caso en solo lectura (el expediente). */
  onAbrirCaso?: (casoId: string) => void;
  /** Abre la evaluación en solo lectura, en la pestaña «Informe FAR-03», con la tarjeta «Aprobación». */
  onRevisar: (casoId: string) => void;
  /** Lo que se acaba de decidir en el detalle: el aviso de éxito al volver a la cola. */
  exito?: DecisionDelAprobador | null;
  onCerrarExito?: () => void;
  /** La salida del estado «Sin permiso» (HU-17-C9): la bandeja de quien entró. */
  onIrABandeja?: () => void;
}) {
  const [numero, setNumero] = useState('');
  const numeroDiferido = useDiferido(numero);
  const [filas, setFilas] = useState<InformeEnAprobacion[] | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargando, setCargando] = useState(true);
  /** true = el servidor no tiene `/evaluacion/informes` y se está leyendo la bandeja del Comité. */
  const [desdeBandeja, setDesdeBandeja] = useState(false);
  const secuencia = useRef(0);

  const cargar = useCallback(async () => {
    const mia = ++secuencia.current;
    setCargando(true);
    setFallo(null);
    try {
      let lista: InformeEnAprobacion[];
      let replegado = false;
      try {
        lista = (await listarInformesEnAprobacion()).filter((f) => contieneElNumero(f, numeroDiferido));
      } catch (e) {
        if (!esNoEncontrado(e)) throw e;
        // Backend viejo: la cola era una lectura filtrada de la bandeja del Comité.
        replegado = true;
        const bandeja = await consultarBandeja(numeroDiferido ? { numero: numeroDiferido } : {});
        lista = bandeja.bloques.flatMap((b) => b.filas).filter((f) => ESTADOS_DEL_APROBADOR.has(f.estado_del_caso)).map(desdeLaBandeja);
      }
      if (mia !== secuencia.current) return;
      setDesdeBandeja(replegado);
      setFilas(lista);
    } catch (e) {
      if (mia !== secuencia.current) return;
      setFallo(aFallo(e));
    } finally {
      if (mia === secuencia.current) setCargando(false);
    }
  }, [numeroDiferido]);

  useEffect(() => { cargar(); }, [cargar]);

  const { porAprobar, observados } = useMemo(() => ({
    porAprobar: (filas ?? []).filter((f) => f.estado_del_informe !== 'observado'),
    observados: (filas ?? []).filter((f) => f.estado_del_informe === 'observado'),
  }), [filas]);

  return (
    <>
      <PageHeader
        eyebrow="Aprobación · Informes FAR-03"
        hu="HU-16 · HU-18 · C-05"
        title="Numeración y aprobación"
        subtitle="Los informes que el Comité envió a aprobación. Observar los devuelve al Comité con el motivo."
        icon={FileCheck2}
        actions={<Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => { cargar(); }} disabled={cargando}>Refrescar</Button>}
      />
      {/* El aviso de lo que se acaba de decidir va arriba: es lo primero que se lee al volver. */}
      {exito && (
        <div className="mb-4">
          <Alert data-testid="aprobacion-exito" tone="success" title={exito.texto} onCerrar={onCerrarExito}>
            {exito.aprobado && onAbrirCaso && (
              <Button data-testid="aprobacion-exito-ver-expediente" size="sm" variant="secondary" icon={FolderOpen}
                onClick={() => onAbrirCaso(exito.casoId)}>
                Ver expediente
              </Button>
            )}
          </Alert>
        </div>
      )}
      {/* Sin permiso no hay cola que buscar: el buscador no se ofrece (§8.1 R4). */}
      {fallo?.estado !== 403 && (
      <Card className="mb-4" title="Buscar">
        <div className="flex flex-wrap items-center gap-3">
          <Input aria-label="Buscar por número de caso" placeholder="Buscar por N.º de caso (PE-002-2026)" className="!w-80 max-w-full"
            value={numero} onChange={(e) => setNumero(e.target.value)} />
          {filas && !cargando && (
            <p className="text-meta text-ink-soft" aria-live="polite">
              <Mono className="text-ink">{filas.length}</Mono> {filas.length === 1 ? 'informe' : 'informes'} en aprobación
            </p>
          )}
        </div>
        {desdeBandeja && !cargando && (
          <p className="text-meta text-ink-soft mt-2" role="status">
            Solo se muestran los informes de los casos que siguen en la bandeja del Comité.
          </p>
        )}
      </Card>
      )}

      {fallo && fallo.estado === 403 && (
        <EmptyState data-testid="aprobacion-sin-permiso" icon={ShieldX}
          title="Solo la Vicepresidencia y la Gerencia General aprueban el informe" description={fallo.message}
          action={onIrABandeja && <Button variant="secondary" onClick={onIrABandeja}>Volver a la bandeja</Button>} />
      )}
      {fallo && fallo.estado !== 403 && (
        <Alert data-testid="aprobacion-error" tone="danger" title="No se pudo cargar la cola de aprobación" detalleTecnico={fallo.detalleTecnico}>
          {fallo.message}{fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
          <div className="mt-2"><Button size="sm" variant="secondary" icon={RefreshCw} onClick={() => { cargar(); }}>Reintentar</Button></div>
        </Alert>
      )}
      {cargando && !filas && <p data-testid="aprobacion-cargando" className="p-6 text-body text-ink-soft">Cargando los informes…</p>}

      {filas && !fallo && (
        <>
          <Bloque testid="aprobacion-bloque-por-aprobar" titulo="Por aprobar" filas={porAprobar} onAbrir={onAbrirCaso} onRevisar={onRevisar}
            vacio={numero
              ? { icono: Search, titulo: 'Ningún informe por aprobar lleva ese número', descripcion: 'Prueba con otro número.' }
              // Estado «Vacío» de la spec (`aprobacion-vacio`): la cola entera sin informes.
              : { icono: Inbox, titulo: 'No hay informes por aprobar', descripcion: 'Cuando el Comité envíe un FAR-03 a aprobación aparecerá aquí.',
                testid: filas.length === 0 ? 'aprobacion-vacio' : undefined }} />
          <Bloque testid="aprobacion-bloque-observados" titulo="Observados, esperando al Comité" filas={observados} onAbrir={onAbrirCaso} onRevisar={onRevisar}
            vacio={numero
              ? { icono: Search, titulo: 'Ningún informe observado lleva ese número', descripcion: 'Prueba con otro número.' }
              : { icono: Inbox, titulo: 'No hay informes observados', descripcion: 'Los que devuelvas con observaciones esperan aquí hasta que el Comité los subsane.' }} />
        </>
      )}
    </>
  );
}
